import React, { useMemo, useState, useContext, useEffect } from 'react';
import { Row, Col, Divider, Button, Card } from 'antd';
import { GlobalStateContext } from '../context/GlobalStateProvider';
import { getMappedValue } from './metadata/mappings';
import { formatMoneyAmount } from '../utils/formatUtils';
import { traverseObject } from '../utils/objectUtils';
import { onSubmitAgent } from '../api/applicationAPI';
import {navigate, navigateToHome} from '../navigation/navigationHistory';
import {ConfigContext} from 'portal-core/components/PortalApp';

const getPremiumTotal = (quoteData, totals) => {
  let premiumTotal = 0;
  for (let t of totals){
      const value = getMappedValue(quoteData, t.path);
      if (value === null || value === undefined) return value;
      premiumTotal += parseFloat(value);
  }
  return formatMoneyAmount(premiumTotal);
};

const getPremiumAndFee = (quoteData, path) => {
  const value = getMappedValue(quoteData, path);
  if (value === null || value === undefined) return value;
  return formatMoneyAmount(parseFloat(value))
};

export const collectValidationIssues = (app, issues, issuesByPath) => {
  traverseObject(app, (obj, path) => {
    if (obj.issues) {
      issuesByPath[path] = [];
      obj.issues.forEach((issue) => {
        if (issue.msg) {
          issuesByPath[path].push(issue);
          issues.push(issue);
        }
      });
    }
  });
};

const isOdd = (num) => num % 2;

const Details = ({ rows, quoteData, isDesktop }) => {
  const leftData = rows[0];
  const rightData = rows[1];

  const LeftSide = () => {
    return leftData.map((data, index) => {
      const { span, label, path } = data;
      const value = getMappedValue(quoteData, path);

      return (
        <Col
          span={span}
          key={index}
          style={{
            textAlign: !isDesktop && isOdd(index) ? 'end' : 'left',
          }}
        >
          <p style={{ margin: 0, paddingBottom: '4px' }}>{label}</p>
          <p style={{ fontWeight: 'bold' }}>{value}</p>
        </Col>
      );
    });
  };

  const RightSide = () => {
    let isNotYetCalculated = false;
    return rightData.map((data, index) => {
      if (data.path === 'divider') {
        return (
          <Col span={data.span} key={index}>
            <Divider style={{ marginTop: '4px', marginBottom: '4px' }} />
          </Col>
        );
      }

      const { span, label, path, totals } = data;
      let value =
        totals && totals.length > 0
          ? getPremiumTotal(quoteData, totals)
          : getPremiumAndFee(quoteData, path);
      //BasicPolicy.WrittenPremiumAmt = 0.00 || BasicPolicy.WrittenPremiumAmt === null
      if (path === "basicPolicy.writtenPremiumAmt" && (value === null || value === undefined || value === "$0.00")){
        isNotYetCalculated = true;
      }
      if (isNotYetCalculated) {
        value = 'Not Yet Calculated';
      }

      return (
        <Col
          span={span}
          key={index}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <p style={{ margin: 0, paddingBottom: '4px' }}>{label}</p>
          <p style={{ margin: 0, fontWeight: 'bold' }} id={label}>{value}</p>
        </Col>
      );
    });
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={!isDesktop ? 24 : 16}>
        <LeftSide />
      </Col>
      <Col span={!isDesktop ? 24 : 8}>
        <RightSide />
      </Col>
    </Row>
  );
};

const Errors = ({ errors, changeSummaryLayout }) => {
  const types = ['Approval', 'Error', 'Warning', 'Other'];

  const sortedCollection = types.reduce((acc, type) => {
    const matchingTypes =
      type !== 'Other'
        ? errors.filter((error) => error.typeCd === type)
        : errors.filter((error) => {
            return (
              error.typeCd !== 'Approval' &&
              error.typeCd !== 'Error' &&
              error.typeCd !== 'Warning'
            );
          });
    acc.push(...matchingTypes);
    return acc;
  }, []);

  if (changeSummaryLayout) {
    return (
      <div>
        {sortedCollection.length
          ? sortedCollection.map((error, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: isOdd(index) ? '#f8f9fa' : '',
                  padding: '8px 16px',
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <p style={{ fontWeight: 'bold', margin: 0 }}>Type</p>
                  </Col>
                  <Col span={24} style={{ marginTop: '-8px' }}>
                    <p>{error.typeCd}</p>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <p style={{ fontWeight: 'bold', margin: 0 }}>Description</p>
                  </Col>
                  <Col span={24} style={{ marginTop: '-8px' }}>
                    <p>{error.msg}</p>
                  </Col>
                </Row>
              </div>
            ))
          : null}
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={4}>
          <p style={{ fontWeight: 'bold' }}>Type</p>
        </Col>
        <Col span={20}>
          <p style={{ fontWeight: 'bold' }}>Description</p>
        </Col>
      </Row>
      {sortedCollection.length
        ? sortedCollection.map((error, index) => (
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <p>{error.typeCd}</p>
              </Col>
              <Col span={20}>
                <p>{error.msg}</p>
              </Col>
            </Row>
          ))
        : null}
    </div>
  );
};

const ErrorBanner = ({ quoteNumber, errorMessage }) => {
  const { actionTitle, steps } = errorMessage;

  return (
    <div style={styles.errorContainer}>
      <div style={{ display: 'flex', alignContent: 'center' }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <path
            fill="#28333F"
            fill-rule="evenodd"
            d="M11.99,2 C17.52,2 22,6.48 22,12 C22,17.52 17.52,22 11.99,22 C6.47,22 2,17.52 2,12 C2,6.48 6.47,2 11.99,2 Z M12,4 C7.58,4 4,7.58 4,12 C4,16.42 7.58,20 12,20 C16.42,20 20,16.42 20,12 C20,7.58 16.42,4 12,4 Z M12,11 C12.55,11 13,11.45 13,12 L13,16 C13,16.55 12.55,17 12,17 C11.45,17 11,16.55 11,16 L11,12 C11,11.45 11.45,11 12,11 Z M13,7 L13,9 L11,9 L11,7 L13,7 Z"
          />
        </svg>
        <p style={{ fontWeight: 'bold' }}>
          Warning: Errors have been raised for quote {quoteNumber}.
        </p>
      </div>
      <div>
        <p>{actionTitle}</p>
      </div>
      {steps.length
        ? steps.map((step, index) => (
            <div key={index}>
              <p>- {step.value}</p>
            </div>
          ))
        : null}
    </div>
  );
};

export const RetrieveQuoteSummary = ({ details, errorMessage }) => {
  const { isDesktop, changeSummaryLayout } = useContext(GlobalStateContext);

  const config = useContext(ConfigContext);
  let influencerChanges = sessionStorage.getItem('b1_sales_influencer_id') == config.branding.influencer1.id;
  useEffect(() => {
      updateBranding(influencerChanges);
  });
  const referAgentText = config.branding.buttons.referAgent ? config.branding.buttons.referAgent : 'Refer to Agent';

  const { data: quoteData } = history.state;
  const shouldDisplayAgentButton = false;
  const [state, setState] = useState({
    data: null,
    errors: null,
    loadingCancel: false,
    loadingRefer: false,
    loadingEdit: false,
    disabled: false,
  });

  const issues = useMemo(() => {
    const all = [];
    const byPath = {};
    quoteData && collectValidationIssues(quoteData, all, byPath);
    state.errors && state.errors.forEach((err) => all.push(err));
    return { all, byPath };
  }, [quoteData, state.errors]);

  const quoteNumber = quoteData.applicationNumber;

  const handleCancel = () => {
    setState({
      loadingCancel: true,
      disabled: true,
    });
    return setTimeout(() => {
      setState({ loadingCancel: false });
      navigateToHome();
    }, 1000);
  };

  const handleReferAgent = () => {
    setState({
      loadingRefer: true,
      disabled: true,
    });
      onSubmitAgent(quoteData.systemId, quoteData.applicationNumber);
  };

  const handleEditQuote = () => {
    setState({
      loadingEdit: true,
      disabled: true,
    });
    return setTimeout(() => {
      setState({ loadingEdit: false, disabled: false });
      navigate(`/application/${quoteData.systemId}`);
    }, 1000);
  };

  return (
    <Card
      style={{ width: changeSummaryLayout ? '90%' : '60%', margin: 'auto' }}
    >
      {issues.all.length ? (
        <ErrorBanner quoteNumber={quoteNumber} errorMessage={errorMessage} />
      ) : null}
      <div style={{ paddingLeft: '8px' }}>
        <h2>Details</h2>
      </div>
      <Divider />
      <Details rows={details} quoteData={quoteData} isDesktop={isDesktop} />
      {issues.all.length ? (
        <div style={{ paddingTop: '16px' }}>
          <div style={{ paddingLeft: '8px' }}>
            <h2>Validation/Underwriting Errors</h2>
          </div>
          <Divider />
          <Errors
            errors={issues.all}
            changeSummaryLayout={changeSummaryLayout}
          />
        </div>
      ) : null}
      <Divider />

      <div style={styles.buttonContainer}>
        <div>
          {!influencerChanges && (
          <Button
            disabled={state.disabled}
            type="link"
            onClick={handleCancel}
            id={'cancelButton'}
            loading={state.loadingCancel}
          >
            Cancel
          </Button>
          )}
          {influencerChanges && (
          <Button
            disabled={state.disabled}
            type="link"
            onClick={handleCancel}
            id={'cancelButton'}
            loading={state.loadingCancel}
            className="cobranded-button-link"
          >
            Cancel
          </Button>
          )}
        </div>
        <div>
          {shouldDisplayAgentButton
          && (<Button
                disabled={state.disabled}
                type="secondary"
                className="secondary-button"
                onClick={handleReferAgent}
            >
              {referAgentText}
            </Button>)
          }
          <Button
            disabled={state.disabled}
            style={{ marginLeft: '8px' }}
            type="primary"
            onClick={handleEditQuote}
          >
            Edit Quote
          </Button>
        </div>
      </div>
    </Card>
  );
};

function updateBranding(influencerChanges){
    if (influencerChanges) {
        const primaryButtons = document.querySelectorAll('.ant-btn-primary');
        primaryButtons.forEach(element => {
          element.classList.add('cobranded-primary-button');
          element.classList.remove('ant-btn-primary');
        });
        const secondaryButtons = document.querySelectorAll('.secondary-button');
        secondaryButtons.forEach(element => {
          element.classList.add('cobranded-secondary-button');
          element.classList.remove('secondary-button');
        });
        const linkButtons = document.querySelectorAll('.ant-btn-link');
        linkButtons.forEach(element => {
          element.classList.add('cobranded-button-link');
          element.classList.remove('ant-btn-link');
        });
    }
}

const styles = {
  errorContainer: {
    backgroundColor: '#ff9d2e',
    padding: '16px',
    marginBottom: '16px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};